import * as React from "react";
import { useForm } from "react-hook-form";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useMutation } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { MoneyInput } from "@/components/ui/money-input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Loader2, X } from "lucide-react";
import { useTenant } from "@/contexts/TenantContext";
import { logCrud } from "@/services/activities";

const productSchema = z.object({
  nome: z.string().min(1, "Nome é obrigatório"),
  preco_venda: z.number().min(0, "Preço inválido").default(0),
  preco_custo: z.number().min(0, "Preço inválido").default(0),
  unit_measurement_purchase_id: z.string().uuid().optional().or(z.literal("")),
  unit_measurement_sale_id: z.string().uuid().optional().or(z.literal("")),
});

type ProductFormData = z.infer<typeof productSchema>;

interface QuickAddProductModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onProductCreated?: (product: { id: string; nome: string; preco_venda: number; unit_measurement_purchase_id?: string | null; unit_measurement_sale_id?: string | null }) => void;
}

export function QuickAddProductModal({ open, onOpenChange, onProductCreated }: QuickAddProductModalProps) {
  const queryClient = useQueryClient();
  const { tenantId } = useTenant();

  const form = useForm<ProductFormData>({
    resolver: zodResolver(productSchema),
    defaultValues: {
      nome: "",
      preco_venda: 0,
      preco_custo: 0,
      unit_measurement_purchase_id: "",
      unit_measurement_sale_id: "",
    },
  });

  const { data: unitMeasurements = [] } = useQuery({
    queryKey: ["unit_measurements"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("unit_measurements" as any)
        .select("id, nome, slug")
        .order("nome", { ascending: true });
      if (error) throw error;
      return (data || []) as unknown as { id: string; nome: string; slug: string }[];
    },
  });

  // Define padrão 'und' para compra e venda quando unidades carregarem
  React.useEffect(() => {
    if (!unitMeasurements?.length) return;
    const und = unitMeasurements.find((u) => u.slug === "und");
    if (!und) return;
    const purchase = form.getValues("unit_measurement_purchase_id");
    const sale = form.getValues("unit_measurement_sale_id");
    if (!purchase) form.setValue("unit_measurement_purchase_id", und.id, { shouldDirty: true });
    if (!sale) form.setValue("unit_measurement_sale_id", und.id, { shouldDirty: true });
  }, [unitMeasurements, form]);

  const createProductMutation = useMutation({
    mutationFn: async (data: ProductFormData) => {
      const payload: any = {
        nome: data.nome,
        preco_venda: data.preco_venda,
        status: "ativo",
        quantity_decimal_places: 0, // Valor padrão
      };
      if (tenantId) payload.tenant_id = tenantId;
      if (typeof data.preco_custo === "number") payload.preco_custo = data.preco_custo;
      // unit_measurement desabilitado temporariamente
      // payload.unit_measurement_purchase_id = data.unit_measurement_purchase_id || null;
      // payload.unit_measurement_sale_id = data.unit_measurement_sale_id || null;

      const { data: product, error } = await supabase
        .from("products")
        .insert([payload])
        .select()
        .single();

      if (error) throw error;
      return product;
    },
    onSuccess: async (product) => {
      toast.success("Produto cadastrado com sucesso!");
      await queryClient.invalidateQueries({ queryKey: ["products"] });
      if (product?.id) {
        void logCrud({ entitySlug: "entity:products", operationSlug: "create", entityId: product.id, entityName: product.nome, screenSlug: "produtos" });
      }
      onProductCreated?.({
        id: product.id,
        nome: product.nome,
        preco_venda: Number(product.preco_venda) || 0,
        unit_measurement_purchase_id: null,
        unit_measurement_sale_id: null,
      });
      onOpenChange(false);
      form.reset();
    },
    onError: (error: Error) => {
      toast.error("Erro ao cadastrar produto: " + error.message);
    },
  });

  const onSubmit = (data: ProductFormData) => {
    createProductMutation.mutate({
      ...data,
      preco_venda: Number(data.preco_venda || 0),
      preco_custo: Number(data.preco_custo || 0),
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md p-0 overflow-hidden">
        <div className="flex max-h-[80vh] flex-col">
          <DialogHeader className="sticky top-0 z-10 bg-background px-6 py-4 border-b relative">
            <DialogTitle>Cadastro Rápido de Produto</DialogTitle>
            <Button
              variant="ghost"
              size="icon"
              className="absolute right-4 top-4 h-6 w-6 text-destructive hover:text-destructive hover:bg-destructive/10"
              onClick={() => onOpenChange(false)}
            >
              <X className="h-4 w-4" />
            </Button>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto px-6 py-4">
            <Form {...form}>
              <form id="quick-product-form" onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                <FormField
                  control={form.control}
                  name="nome"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Nome *</FormLabel>
                      <FormControl>
                        <Input {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="preco_venda"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Preço de Venda</FormLabel>
                        <FormControl>
                          <MoneyInput
                            value={field.value ?? 0}
                            onValueChange={(value) => field.onChange(value)}
                            onBlur={field.onBlur}
                            placeholder="0,00"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="preco_custo"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Preço de Custo</FormLabel>
                        <FormControl>
                          <MoneyInput
                            value={field.value ?? 0}
                            onValueChange={(value) => field.onChange(value)}
                            onBlur={field.onBlur}
                            placeholder="0,00"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="unit_measurement_purchase_id"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Unidade de Compra</FormLabel>
                        <FormControl>
                          <Select value={field.value || ""} onValueChange={field.onChange}>
                            <SelectTrigger>
                              <SelectValue placeholder="Selecione a unidade de compra" />
                            </SelectTrigger>
                            <SelectContent>
                              {unitMeasurements.map((u) => (
                                <SelectItem key={u.id} value={u.id}>{`${u.nome} (${u.slug})`}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="unit_measurement_sale_id"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Unidade de Venda</FormLabel>
                        <FormControl>
                          <Select value={field.value || ""} onValueChange={field.onChange}>
                            <SelectTrigger>
                              <SelectValue placeholder="Selecione a unidade de venda" />
                            </SelectTrigger>
                            <SelectContent>
                              {unitMeasurements.map((u) => (
                                <SelectItem key={u.id} value={u.id}>{`${u.nome} (${u.slug})`}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </form>
            </Form>
          </div>

          <div className="bg-background px-6 py-4 border-t flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancelar
            </Button>
            <Button type="submit" form="quick-product-form" disabled={createProductMutation.isPending}>
              {createProductMutation.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
              Cadastrar
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
